import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Pencil, Trash2, Landmark, GitBranch, Unlink, AlertTriangle, RefreshCw } from 'lucide-react';
import { fmtFecha } from '../../../lib/utils';
import { fmtCLP, CONCILIACION_BADGE, TIPO_LABEL, EstadoBadge, MODOS } from './egresosUtils';

// Iter 397: explica cómo se determinó la hora de creación del comprobante (regla 14:00).
const FUENTE_LABEL = { erp_cxp: 'hora exacta (pago creado por el ERP)', erp_directo: 'hora exacta (creado desde el ERP)', acotado: 'acotada por el id de pago de Alegra', incierta: 'hora no determinable → se asume antes de las 14:00' };
function horaCreacionTitulo(e) {
  if (e.fecha_cartola_esperada_manual) return 'Fecha esperada fijada manualmente';
  if (e.numero_cheque) return 'Cheque: se concilia por número';
  const hora = e.hora_creacion_alegra ? new Date(e.hora_creacion_alegra).toLocaleString('es-CL', { timeZone: 'America/Santiago', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }) : null;
  const partes = [`Hora de creación del comprobante: ${hora || 'desconocida'}`, FUENTE_LABEL[e.hora_creacion_fuente] || ''];
  if (e.hora_creacion_detalle) partes.push(e.hora_creacion_detalle);
  return partes.filter(Boolean).join(' · ');
}

export function EgresosTable({ items, modo = 'egreso', onEditarOC, onConciliar, onDesconciliar, onTrazabilidad, onCancelar, onRefrescar, refrescandoId, hoy }) {
  const M = MODOS[modo];
  const tid = M.key;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{modo === 'ingreso' ? 'Fecha cobro' : 'Fecha pago'}</TableHead>
          <TableHead>Cartola esp.</TableHead>
          <TableHead>Código</TableHead>
          <TableHead>{M.contraparte}</TableHead>
          <TableHead>Tipo</TableHead>
          <TableHead>{M.conOC ? 'OC' : 'Facturas'}</TableHead>
          <TableHead className="text-right">Monto</TableHead>
          {M.conOC && <TableHead className="text-right">Disponible</TableHead>}
          <TableHead>Aplicación</TableHead>
          <TableHead>Banco</TableHead>
          <TableHead>{M.docLabel} N°</TableHead>
          <TableHead></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((e) => {
          const atrasado = e.estado_conciliacion_banco === 'pendiente' && e.fecha_cartola_esperada && hoy && e.fecha_cartola_esperada < hoy;
          const puedeEditarOC = M.conOC && ['pendiente', 'parcial'].includes(e.estado_aplicacion);
          const facturas = e[M.conFacturasAplicadas] || [];
          return (
            <TableRow key={e.id} data-testid={`${tid}-row-${e.id}`} className={e.estado_conciliacion_banco === 'observado' ? 'bg-orange-50/60' : ''}>
              <TableCell className="text-xs whitespace-nowrap">{fmtFecha(e.fecha_pago)}</TableCell>
              <TableCell className="text-xs whitespace-nowrap" title={horaCreacionTitulo(e)}>
                {(e.fecha_cartola_esperada && fmtFecha(e.fecha_cartola_esperada)) || (e.numero_cheque ? <span className="text-muted-foreground">cheque</span> : '—')}
                {atrasado && <AlertTriangle className="inline h-3 w-3 ml-1 text-red-500" title="Aún no aparece en cartola" />}
                {e.fecha_cartola_esperada && e.fecha_pago && e.fecha_cartola_esperada !== e.fecha_pago && !e.fecha_cartola_esperada_manual && (
                  <span className="ml-1 text-[9px] text-sky-700" title={horaCreacionTitulo(e)} data-testid={`${tid}-tarde-${e.id}`}>+14h</span>
                )}
              </TableCell>
              <TableCell className="font-mono text-xs" data-testid={`${tid}-codigo-${e.id}`} title={horaCreacionTitulo(e)}>{e.codigo_conciliacion || '—'}</TableCell>
              <TableCell className="text-xs max-w-[220px]">
                <div className="truncate" title={e.razon_social}>{e.razon_social || '—'}</div>
                <div className="text-[10px] text-muted-foreground font-mono truncate" title={`Nombre conciliación: ${e.nombre_conciliacion || ''}`}>
                  {e[M.rutCampo]} · {e.nombre_conciliacion}
                </div>
              </TableCell>
              <TableCell className="text-[10px]">{TIPO_LABEL[e.tipo] || e.tipo || '—'}</TableCell>
              <TableCell className="font-mono text-xs">
                {M.conOC ? (puedeEditarOC ? (
                  <button type="button" onClick={() => onEditarOC(e)} className="inline-flex items-center gap-1 hover:underline text-left"
                    title="Asociar OC: al contabilizar su factura el anticipo se aplica automáticamente" data-testid={`btn-oc-egreso-${e.id}`}>
                    {e.oc_numero || <span className="text-muted-foreground">+ OC</span>}
                    <Pencil className="h-3 w-3 text-muted-foreground" />
                  </button>
                ) : (e.oc_numero || '—')) : (
                  <span title={facturas.map((f) => `${f.numero} ${fmtCLP(f.monto)}`).join('\n')} data-testid={`ingreso-facturas-${e.id}`}>
                    {facturas.length ? facturas.map((f) => f.numero).filter(Boolean).slice(0, 3).join(', ') + (facturas.length > 3 ? ` +${facturas.length - 3}` : '') : <span className="text-muted-foreground">—</span>}
                  </span>
                )}
              </TableCell>
              <TableCell className={`text-right tabular-nums text-xs ${modo === 'ingreso' ? 'text-emerald-700' : ''}`}>{fmtCLP(e.monto)}</TableCell>
              {M.conOC && (
                <TableCell className="text-right tabular-nums text-xs font-bold text-emerald-700">
                  {['pendiente', 'parcial'].includes(e.estado_aplicacion) ? fmtCLP(e.monto_disponible) : <span className="text-muted-foreground font-normal">—</span>}
                </TableCell>
              )}
              <TableCell><EstadoBadge map={M.aplicacionBadge} estado={e.estado_aplicacion} testId={`badge-aplicacion-${e.id}`} /></TableCell>
              <TableCell>
                <EstadoBadge map={CONCILIACION_BADGE} estado={e.estado_conciliacion_banco} testId={`badge-banco-${e.id}`} />
                {e.fecha_conciliacion_banco && <div className="text-[10px] text-muted-foreground mt-0.5">{fmtFecha(e.fecha_conciliacion_banco)}</div>}
              </TableCell>
              <TableCell className="text-xs">
                {e.alegra_payment_id ? <Badge variant="outline" className="font-mono text-[10px]" title={`id Alegra ${e.alegra_payment_id}`} data-testid={`${tid}-numero-${e.id}`}>{e.alegra_number ? `N°${e.alegra_number}` : `#${e.alegra_payment_id}`}</Badge> : <span className="text-muted-foreground text-[10px]">—</span>}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                <Button size="sm" variant="ghost" onClick={() => onTrazabilidad(e)} className="h-7 w-7 p-0" title={M.conOC ? 'Trazabilidad OC → Egreso → Banco → Factura' : 'Trazabilidad Factura → Cobro → Banco'} data-testid={`btn-traza-${e.id}`}>
                  <GitBranch className="h-3.5 w-3.5 text-indigo-600" />
                </Button>
                {onRefrescar && e.alegra_payment_id && (
                  <Button size="sm" variant="ghost" onClick={() => onRefrescar(e)} disabled={refrescandoId === e.id} className="h-7 w-7 p-0" title="Actualizar desde Alegra (monto, número, estado)" data-testid={`btn-refrescar-${e.id}`}>
                    <RefreshCw className={`h-3.5 w-3.5 text-sky-700 ${refrescandoId === e.id ? 'animate-spin' : ''}`} />
                  </Button>
                )}
                {['pendiente', 'observado'].includes(e.estado_conciliacion_banco) && (
                  <Button size="sm" variant="ghost" onClick={() => onConciliar(e)} className="h-7 w-7 p-0" title="Conciliar con la cartola" data-testid={`btn-conciliar-${e.id}`}>
                    <Landmark className={`h-3.5 w-3.5 ${e.estado_conciliacion_banco === 'observado' ? 'text-orange-600' : 'text-sky-600'}`} />
                  </Button>
                )}
                {e.estado_conciliacion_banco === 'conciliado' && (
                  <Button size="sm" variant="ghost" onClick={() => onDesconciliar(e)} className="h-7 w-7 p-0" title="Desconciliar (el movimiento vuelve a pendiente)" data-testid={`btn-desconciliar-${e.id}`}>
                    <Unlink className="h-3.5 w-3.5 text-orange-500" />
                  </Button>
                )}
                {onCancelar && e.estado_aplicacion === 'pendiente' && e.estado_conciliacion_banco !== 'conciliado' && (
                  <Button size="sm" variant="ghost" onClick={() => onCancelar(e)} className="h-7 w-7 p-0" title="Cancelar" data-testid={`btn-cancelar-${e.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
